import { getCoreUtils, nz, StringOnlyOf } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppIndex, AppRow } from "./AppIndex";
import { AppImportMemory } from "./AppImportStatus";
import { AppTaskActions } from "./AppTaskActions";
import { AppTaskApi } from "./AppTaskApi";
import { AppTaskBoard } from "./AppTaskBoard";
import { AppTaskFrame } from "./AppTaskFrame";
import { AppTaskNotifier } from "./AppTaskNotify";
import { AppTaskRules } from "./AppTaskRules";
import { AppChatAppend } from "./AppChatAppend";
import { AppAssistEntry, AppAssistSession } from "./AppAssistRules";
import { AppAssistPanel } from "./AppAssistPanel";
import { AppAiChat } from "./AppAiChat";
import { IRxLater } from "./AppReactions";
import { AppTranslateChip } from "./AppTranslateChip";
import { AppTranslator } from "./AppTranslator";

// AppTabBus - SEVERAL OPEN TABS KEEP EACH OTHER UP TO DATE (owner, 2026-09-27: "The user will multi-task
// a lot, switch between multiple people. Also investigate how multiple open browser tabs will work.").
//
// One `BroadcastChannel` per subscriber (the name is a hash of the number, never the number) carries
// NOTICES between the tabs of one browser:
//   task    {href, taskId, state, until}  - `/tasks` confirmed a change (the strip / board update)
//   acted   {taskId, outcome}             - a "Let <name> know?" card was sent, edited or skipped
//   aichat  {href}                        - the AI history of that chat was saved (the panel re-reads it)
//   outcome {href, id, outcome}           - a panel draft was sent / taken into the box
//   sent    {chatId, id}                  - a message was queued (its bubble appears in that chat)
//   translate {href}                      - that chat's translation settings were saved (re-read them)
//
// ==================================================================================================
// !! ⛔ DECISION 9: A NOTICE CAN ONLY CHANGE WHAT IS SHOWN
// ==================================================================================================
//
// No handler here reaches a sender - not `AppComposer.SendText`, `QueueText`, `QueueTo`,
// `QueueReaction`, the notifier's `Act`, `/send` or `/tasks`. A notice withdraws a card, repaints a
// row, marks a draft as used, or READS (the sent history, the AI history). `AppTabBus.test.ts` dispatches
// every kind against real notifier/actions/appender objects with spies on every sender, and greps this
// file for the sender names.
// ⛔ ANOTHER TAB IS SAME-ORIGIN BUT ITS MESSAGE IS STILL UNTRUSTED INPUT: every field is checked against an
// allow-list (`AppTabBusRules.Parse`) and nothing is ever written as markup - the receivers set attributes,
// state fields and `textContent` through the classes that already do so.
// ⚠ Where `BroadcastChannel` is missing (or throws - a locked-down browser), everything here does nothing:
// each tab works alone, exactly as before.
// ⚠ PRIVATE DATA: task labels, questions and drafts never travel - only ids, states and hrefs.

/** One notice between tabs. Unused fields are "". */
export class AppTabNotice {
    public Kind: string = "";
    public Href: string = "";
    public TaskId: string = "";
    public State: string = "";
    public Until: string = "";
    public Outcome: string = "";
    /** An AI-history item id (`c-...`) for `outcome`, a send request id (`s-...`) for `sent`. */
    public Id: string = "";
    public ChatId: string = "";
}

export class AppTabBusRules {
    public static readonly Version: number = 1;
    public static readonly Prefix: string = "wac-";
    public static readonly Kinds: string[] = ["task", "acted", "aichat", "outcome", "sent", "translate"];
    public static readonly Outcomes: string[] = ["sent", "edited", "skipped"];
    public static readonly MaxHref: number = 512;
    /** A send request id - the shape `AppOutbox.NewId` writes and `SendRules.IdOk` accepts. */
    public static readonly RequestIdPattern: RegExp = /^s-[0-9]{14}-[0-9a-f]{1,16}$/;
    /** A 1:1 WhatsApp chat id: a number or a privacy alias. ⛔ Never a group. */
    public static readonly ChatIdPattern: RegExp = /^[0-9]{5,20}@(s\.whatsapp\.net|lid)$/;
    /**
     * Control characters, markup and backslashes never belong in an href. ⚠ BUILT FROM A STRING: the edit
     * tooling has turned escapes inside a regex LITERAL into raw characters before (`HeaderSafe`).
     */
    public static readonly BadHrefChar: RegExp = new RegExp("[\\u0000-\\u001f\\u007f<>\"\\\\]");
    /** Two notices alike inside this window are one. */
    public static readonly SameMs: number = 2000;
    /** The index is re-read on coming back to a tab only when it is older than this. */
    public static readonly IndexStaleMs: number = 60000;
    /** The open chat's AI history is re-read on coming back at most this often. */
    public static readonly HistoryStaleMs: number = 30000;
    /** How long a burst of notices is gathered before the (reading) refresh they ask for. */
    public static readonly GatherMs: number = 400;
    public static readonly SentGatherMs: number = 1500;

    /**
     * The channel's name for this subscriber: "wac-" + an FNV-1a hash of the number's digits, "" with no
     * number. ⚠ A hash, not the number: the name is visible to any script of the origin, and two
     * subscribers signed in on one browser (one after the other) must never hear each other.
     * DOCUMENTED EXCEPTION: pure arithmetic. ERROR-SUPPRESSED-JUSTIFIED: "" (no channel) IS the safe answer.
     */
    public static ChannelName = (Phone: string): string => {
        try {
            const Digits: string = nz(Phone).replace(/[^0-9]/g, "");
            if (Digits.length < 5) { return ""; }
            // The ONE FNV-1a over a number's digits (`AppImportMemory.Who`), padded to 8 hex here.
            return AppTabBusRules.Prefix + ("00000000" + AppImportMemory.Who(Digits)).slice(-8);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: no channel name = this tab works alone, as before - never an error.
            return "";
        }
    };

    /** The message for a notice, or null when the notice is not one this bus carries. */
    public static Build = (Notice: AppTabNotice): Record<string, unknown> | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Out: Record<string, unknown> = { v: AppTabBusRules.Version, kind: Notice.Kind, href: Notice.Href,
                taskId: Notice.TaskId, state: Notice.State, until: Notice.Until, outcome: Notice.Outcome,
                id: Notice.Id, chatId: Notice.ChatId };
            if (AppTabBusRules.Parse(Out) === null) { return null; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabBusRules.Build");
            return null;
        }
    };

    /**
     * Read a message from another tab, or null. ⛔ AN ALLOW-LIST: the version, a known kind, and for that
     * kind exactly the fields it needs, each with its own `typeof` and shape. Anything else is dropped.
     */
    public static Parse = (Raw: unknown): AppTabNotice | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Bag: Record<string, unknown> = Raw as Record<string, unknown>;
            if (Bag.v !== AppTabBusRules.Version) { return null; }
            const Out: AppTabNotice = new AppTabNotice();
            Out.Kind = AppTabBusRules.Text(Bag.kind);
            Out.Href = AppTabBusRules.Text(Bag.href);
            Out.TaskId = AppTabBusRules.Text(Bag.taskId);
            Out.State = AppTabBusRules.Text(Bag.state);
            Out.Until = AppTabBusRules.Text(Bag.until);
            Out.Outcome = AppTabBusRules.Text(Bag.outcome);
            Out.Id = AppTabBusRules.Text(Bag.id);
            Out.ChatId = AppTabBusRules.Text(Bag.chatId);
            if (AppTabBusRules.Valid(Out) === false) { return null; }
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a message another tab sent is outside input; an unreadable one
            // is dropped (law 1), never reported - reporting would latch the page over a stray message.
            return null;
        }
    };

    /** DOCUMENTED EXCEPTION: pure checks. ERROR-SUPPRESSED-JUSTIFIED: false (drop it) IS the safe answer. */
    private static Valid = (N: AppTabNotice): boolean => {
        const HrefOk: boolean = AppTabBusRules.HrefOk(N.Href);
        if (N.Kind === "task") {
            if (HrefOk === false || N.Href.length === 0 || AppTaskApi.IdOk(N.TaskId) === false) { return false; }
            if (AppTaskApi.States.indexOf(N.State) < 0) { return false; }
            if (N.State === "snoozed") { return AppTaskApi.DateOk(N.Until) === true; }
            return N.Until.length === 0 && N.Outcome.length === 0 && N.Id.length === 0 && N.ChatId.length === 0;
        }
        if (N.Kind === "acted") {
            return AppTaskApi.IdOk(N.TaskId) === true && AppTabBusRules.Outcomes.indexOf(N.Outcome) >= 0
                && HrefOk === true && N.State.length === 0 && N.Id.length === 0 && N.ChatId.length === 0;
        }
        if (N.Kind === "aichat") {
            return HrefOk === true && N.Href.length > 0 && N.TaskId.length === 0 && N.Id.length === 0 && N.ChatId.length === 0;
        }
        if (N.Kind === "outcome") {
            return HrefOk === true && AppAiChat.IdPattern.test(N.Id) === true
                && AppTabBusRules.Outcomes.indexOf(N.Outcome) >= 0 && N.TaskId.length === 0 && N.ChatId.length === 0;
        }
        if (N.Kind === "translate") {
            return HrefOk === true && N.Href.length > 0 && N.TaskId.length === 0 && N.Id.length === 0 && N.ChatId.length === 0
                && N.State.length === 0 && N.Outcome.length === 0 && N.Until.length === 0;
        }
        if (N.Kind === "sent") {
            return AppTabBusRules.ChatIdPattern.test(N.ChatId) === true && AppTabBusRules.RequestIdPattern.test(N.Id) === true
                && N.Href.length === 0 && N.TaskId.length === 0 && N.Outcome.length === 0;
        }
        return false;
    };

    /**
     * An href as the index writes it: bounded, no control characters, no scheme, no `..` segment. It is
     * only ever COMPARED with the page's own hrefs, but a shape check keeps nonsense out of every map.
     * DOCUMENTED EXCEPTION: pure check. ERROR-SUPPRESSED-JUSTIFIED: false IS the safe answer.
     */
    public static HrefOk = (Href: string): boolean => {
        if (Href.length > AppTabBusRules.MaxHref) { return false; }
        if (AppTabBusRules.BadHrefChar.test(Href) === true) { return false; }
        if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(Href) === true) { return false; }
        return Href.split("/").indexOf("..") < 0;
    };

    /** What makes two notices "the same" for `SameMs`. DOCUMENTED EXCEPTION: string join; cannot throw. */
    public static KeyOf = (N: AppTabNotice): string => {
        return [N.Kind, N.Href, N.TaskId, N.State, N.Until, N.Outcome, N.Id, N.ChatId].join("|");
    };

    /** The `/tasks` action that leaves a task in `State`. DOCUMENTED EXCEPTION: pure lookup. */
    public static ActionOf = (State: string): string => {
        if (State === "done") { return "done"; }
        if (State === "dismissed") { return "dismiss"; }
        if (State === "snoozed") { return "snooze"; }
        return "reopen";
    };

    /**
     * Is the address a list view (Chats, All, Tasks, Money) - where re-reading the index and repainting
     * costs the owner nothing? ⛔ Never a contact page (the frame, the box's text) or a side flow.
     * DOCUMENTED EXCEPTION: pure check. ERROR-SUPPRESSED-JUSTIFIED: false (do not repaint) IS the safe answer.
     */
    public static IsListRoute = (Hash: string): boolean => {
        const First: string = nz(Hash).replace(/^#/, "").split("/")[0];
        return ["", "chats", "all", "tasks", "money"].indexOf(First) >= 0;
    };

    /**
     * Coming back to a tab: re-read the index? Only on a list view, and only when it is older than
     * `IndexStaleMs` - several tabs flicking back and forth must not re-read it every time.
     * DOCUMENTED EXCEPTION: pure arithmetic. ERROR-SUPPRESSED-JUSTIFIED: false IS the safe answer.
     */
    public static ShouldReload = (OnList: boolean, ReadMs: number, NowMs: number): boolean => {
        if (OnList !== true || ReadMs <= 0) { return false; }
        return NowMs - ReadMs >= AppTabBusRules.IndexStaleMs;
    };

    /** DOCUMENTED EXCEPTION: a field as text. ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    private static Text = (Raw: unknown): string => {
        return StringOnlyOf(Raw);
    };
}

/** The part of a `BroadcastChannel` this bus uses - narrowed so the suite drives it with a fake. */
export interface IAppTabChannel {
    postMessage(Data: unknown): void;
    close(): void;
    onmessage: ((Ev: MessageEvent) => void) | null;
}

/** Opens a channel by name, or null where the browser has none. */
export interface IAppTabChannels {
    Open(Name: string): IAppTabChannel | null;
}

/** The browser's channels. ⚠ Feature-detected: no `BroadcastChannel`, or one that throws, is null. */
export class AppTabBrowserChannels implements IAppTabChannels {
    /** ERROR-SUPPRESSED-JUSTIFIED: a browser without the API (or one that refuses it) simply has one tab's view. */
    public Open = (Name: string): IAppTabChannel | null => {
        try {
            if (typeof BroadcastChannel === "undefined") { return null; }
            return new BroadcastChannel(Name) as unknown as IAppTabChannel;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a browser that refuses the channel has one tab's view - by design.
            return null;
        }
    };
}

/** Hears a notice another tab sent (already checked and de-duplicated). */
export interface IAppTabHeard {
    Heard(Notice: AppTabNotice): void;
}

export class AppTabBus {
    private Channels: IAppTabChannels;
    private Channel: IAppTabChannel | null = null;
    private Name: string = "";
    private Seen: Record<string, number> = {};
    private Clock: () => number;
    /** Who hears the other tabs. ⛔ `AppTabSync` - it can only change what is shown. */
    public Listener: IAppTabHeard | null = null;

    public constructor(Channels: IAppTabChannels | null = null, Clock: (() => number) | null = null) {
        if (Channels === null) { this.Channels = new AppTabBrowserChannels(); } else { this.Channels = Channels; }
        if (Clock === null) { this.Clock = Date.now; } else { this.Clock = Clock; }
    }

    /** Join this subscriber's channel (a different number leaves the old one first). */
    public Ensure = (Phone: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Name: string = AppTabBusRules.ChannelName(Phone);
            if (Name === this.Name && (this.Channel !== null || Name.length === 0)) { return; }
            this.Close();
            this.Name = Name;
            if (Name.length === 0) { return; }
            const Made: IAppTabChannel | null = this.Channels.Open(Name);
            if (Made === null) { return; }
            Made.onmessage = this.OnMessage;
            this.Channel = Made;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabBus.Ensure");
        }
    };

    /** Is a channel open? */
    public Open = (): boolean => {
        return this.Channel !== null;
    };

    /** Tell the other tabs. ⚠ Nothing open, or a notice that does not check out: nothing is sent. */
    public Tell = (Notice: AppTabNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Channel === null) { return; }
            const Message: Record<string, unknown> | null = AppTabBusRules.Build(Notice);
            if (Message === null) { return; }
            this.Remember(AppTabBusRules.KeyOf(Notice));
            (this.Channel as IAppTabChannel).postMessage(Message);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a channel that refuses a post (closed under us) costs only the
            // other tabs' freshness; they catch up when they are shown again.
            return;
        }
    };

    public Close = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Channel !== null) {
                (this.Channel as IAppTabChannel).onmessage = null;
                (this.Channel as IAppTabChannel).close();
            }
            this.Channel = null;
            this.Name = "";
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: closing a channel that is already gone is nothing to report.
            this.Channel = null;
        }
    };

    /** A message from another tab: checked, de-duplicated, then heard. */
    public OnMessage = (Ev: MessageEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Ev === null || typeof Ev !== "object") { return; }
            const Notice: AppTabNotice | null = AppTabBusRules.Parse((Ev as MessageEvent).data);
            if (Notice === null) { return; }
            const Key: string = AppTabBusRules.KeyOf(Notice as AppTabNotice);
            // ⚠ `aichat` carries no id, so two DIFFERENT changes of one chat's history (two removals, a save then a
            // removal) look alike: dropping the second inside `SameMs` left that tab showing a question that was gone.
            // Its bursts are gathered by the reader's own timer (`AppTabSync.OnAiChat`), so every one is heard.
            if ((Notice as AppTabNotice).Kind !== "aichat" && this.Recent(Key) === true) { return; }
            this.Remember(Key);
            if (this.Listener !== null) { (this.Listener as IAppTabHeard).Heard(Notice as AppTabNotice); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabBus.OnMessage");
        }
    };

    private Recent = (Key: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            const At: number | undefined = this.Seen[Key];
            return At !== undefined && this.Clock() - At < AppTabBusRules.SameMs;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabBus.Recent");
            return true;
        }
    };

    /** Note `Key` as just seen, and forget the ones long past (the map stays small). */
    private Remember = (Key: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Now: number = this.Clock();
            for (const Old of Object.keys(this.Seen)) {
                if (Now - this.Seen[Old] >= AppTabBusRules.SameMs) { delete this.Seen[Old]; }
            }
            this.Seen[Key] = Now;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabBus.Remember");
        }
    };
}

/** What the page has on screen now - handed over fresh for every notice, so nothing stale is kept here. */
export class AppTabParts {
    public Index: AppIndex = new AppIndex();
    public Actions: AppTaskActions | null = null;
    public Board: AppTaskBoard | null = null;
    public Frame: AppTaskFrame | null = null;
    public Notifier: AppTaskNotifier | null = null;
    public Appender: AppChatAppend | null = null;
    public Sessions: Record<string, AppAssistSession> = {};
    public Panel: AppAssistPanel | null = null;
    public OpenHref: string = "";
    public IsOwner: boolean = false;
    /** The open chat's translation chip (null when none), and the visit's translation state. */
    public Translate: AppTranslateChip | null = null;
    /** The open "Chat settings" dialog's translation editor (null when none) - it may be about another chat than the open one. */
    public TranslateEditor: AppTranslateChip | null = null;
    public Translator: AppTranslator | null = null;
}

/** The page, as the receivers see it: what is on screen, and the clock. ⛔ No sender is offered. */
export interface IAppTabPage {
    Parts(): AppTabParts;
    Now(): Date;
}

/**
 * The receiving half: what a notice from another tab changes HERE. ⛔ Every path below changes what is
 * shown or READS; see the header. Refreshes that read (the sent history, the AI history, the board) are
 * gathered for a moment, so a burst of notices is one refresh.
 */
export class AppTabSync implements IAppTabHeard {
    private Page: IAppTabPage;
    private Timer: IRxLater;
    private BoardTimer: number = 0;
    private SentTimer: number = 0;
    private HistoryTimer: number = 0;
    private SentChat: string = "";
    private HistoryHref: string = "";
    private IndexReadMs: number = 0;
    private HistoryReadMs: number = 0;

    public constructor(Page: IAppTabPage, Timer: IRxLater) {
        this.Page = Page;
        this.Timer = Timer;
    }

    public Heard = (Notice: AppTabNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Notice.Kind === "task") { this.OnTask(Notice); return; }
            if (Notice.Kind === "acted") { this.OnActed(Notice); return; }
            if (Notice.Kind === "aichat") { this.OnAiChat(Notice); return; }
            if (Notice.Kind === "outcome") { this.OnOutcome(Notice); return; }
            if (Notice.Kind === "sent") { this.OnSent(Notice); return; }
            if (Notice.Kind === "translate") { this.OnTranslate(Notice); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.Heard");
        }
    };

    /**
     * Another tab's confirmed task change: the index row, the strip on the open page and the board take
     * the new state; a reopen withdraws this tab's card for it. ⚠ A change of this tab's own still on its
     * way is left alone - its own answer settles it.
     */
    private OnTask = (N: AppTabNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const P: AppTabParts = this.Page.Parts();
            if (P.Actions !== null && (P.Actions as AppTaskActions).Busy("done", N.TaskId) === true) { return; }
            const Row: AppRow | null = AppTaskRules.FindById(P.Index, N.TaskId);
            if (Row !== null && (Row.State !== N.State || Row.Until !== N.Until)) {
                AppTaskRules.Apply(Row as AppRow, AppTabBusRules.ActionOf(N.State), N.Until, this.Page.Now().toISOString());
            }
            if (P.Frame !== null && P.OpenHref === N.Href) { (P.Frame as AppTaskFrame).Mirror(N.TaskId, N.State); }
            if (N.State === "open" && P.Notifier !== null) { (P.Notifier as AppTaskNotifier).Reopened(N.TaskId); }
            this.GatherBoard();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.OnTask");
        }
    };

    /** A card was acted on in another tab: this tab's card for the same task goes. */
    private OnActed = (N: AppTabNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const P: AppTabParts = this.Page.Parts();
            if (P.Notifier !== null) { (P.Notifier as AppTaskNotifier).Reopened(N.TaskId); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.OnActed");
        }
    };

    /** The AI history of `Href` was saved in another tab: re-read it if that chat's panel is on screen. */
    private OnAiChat = (N: AppTabNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const P: AppTabParts = this.Page.Parts();
            if (P.IsOwner !== true || P.Panel === null || P.OpenHref !== N.Href) { return; }
            this.HistoryHref = N.Href;
            if (this.HistoryTimer !== 0) { return; }
            this.HistoryTimer = this.Timer.Later(AppTabBusRules.GatherMs, new AppTabSyncFire(this, "history").Fire);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.OnAiChat");
        }
    };

    /**
     * A panel draft was used in another tab: the same draft here stops offering Send ("Sent" / "Taken into
     * the message box"). ⛔ Marks only; "sent" is never overwritten.
     */
    private OnOutcome = (N: AppTabNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const P: AppTabParts = this.Page.Parts();
            let Hit: AppAssistSession | null = null;
            for (const Href of Object.keys(P.Sessions)) {
                if (N.Href.length > 0 && Href !== N.Href) { continue; }
                for (const Entry of P.Sessions[Href].Entries) {
                    if (AppTabSync.Marks(Entry, N) === true) { Hit = P.Sessions[Href]; }
                }
            }
            if (Hit !== null && P.Panel !== null && (P.Panel as AppAssistPanel).Session === Hit) { (P.Panel as AppAssistPanel).Render(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.OnOutcome");
        }
    };

    /** Does `Entry` carry the draft `N.Id`? Then it takes the outcome (never over "sent"). */
    private static Marks = (Entry: AppAssistEntry, N: AppTabNotice): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Entry.Id !== N.Id) { return false; }
            if (Entry.Outcome !== "sent") { Entry.Outcome = N.Outcome; }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.Marks");
            return false;
        }
    };

    /** Another tab queued a message: the open chat, if it is that one, asks its sent history again. */
    private OnSent = (N: AppTabNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const P: AppTabParts = this.Page.Parts();
            if (P.Appender === null || (P.Appender as AppChatAppend).Chat() !== N.ChatId) { return; }
            this.SentChat = N.ChatId;
            if (this.SentTimer !== 0) { return; }
            this.SentTimer = this.Timer.Later(AppTabBusRules.SentGatherMs, new AppTabSyncFire(this, "sent").Fire);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.OnSent");
        }
    };

    /**
     * Another tab saved a chat's translation settings: this tab forgets what it knew (the next send asks again) and
     * the open chat's chip re-reads them. ⛔ Reads only - a settings change never sends anything.
     */
    private OnTranslate = (N: AppTabNotice): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const P: AppTabParts = this.Page.Parts();
            // What this tab knew is forgotten first; then whatever shows this chat's translation reads it again - the
            // header's chip and the open "Chat settings" dialog's editor share ONE `get` (`AppTranslator.Ask`).
            if (P.Translator !== null) { (P.Translator as AppTranslator).Book.Forget(N.Href); }
            for (const One of [P.Translate, P.TranslateEditor]) {
                if (One !== null && One.Page() === N.Href) { One.Reload(); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.OnTranslate");
        }
    };

    private GatherBoard = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.BoardTimer !== 0) { return; }
            this.BoardTimer = this.Timer.Later(AppTabBusRules.GatherMs, new AppTabSyncFire(this, "board").Fire);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.GatherBoard");
        }
    };

    /** A gathered refresh is due. ⛔ Each one repaints or READS - nothing else. */
    public Due = (What: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const P: AppTabParts = this.Page.Parts();
            if (What === "board") {
                this.BoardTimer = 0;
                if (P.Board !== null) { (P.Board as AppTaskBoard).Refresh(); }
                return;
            }
            if (What === "sent") {
                this.SentTimer = 0;
                if (P.Appender !== null && (P.Appender as AppChatAppend).Chat() === this.SentChat) { (P.Appender as AppChatAppend).Refresh(); }
                return;
            }
            if (What === "history") {
                this.HistoryTimer = 0;
                if (P.IsOwner === true && P.Panel !== null && P.OpenHref === this.HistoryHref) {
                    this.HistoryReadMs = this.Page.Now().getTime();
                    (P.Panel as AppAssistPanel).LoadHistory();
                }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.Due");
        }
    };

    /** The page read the index (a load, a sign-in, or `Visible`'s re-read). */
    public IndexRead = (NowMs: number): void => {
        this.IndexReadMs = NowMs;
    };

    /**
     * The tab is shown again: the board re-counts its days at once (cheap), the open chat's AI history is
     * re-read at most every `HistoryStaleMs`, and the answer says whether the page should re-read the index
     * (`AppTabBusRules.ShouldReload`). ⛔ Reads and repaints only.
     */
    public Visible = (NowMs: number, OnList: boolean): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const P: AppTabParts = this.Page.Parts();
            if (P.Board !== null) { (P.Board as AppTaskBoard).Refresh(); }
            if (P.IsOwner === true && P.Panel !== null && NowMs - this.HistoryReadMs >= AppTabBusRules.HistoryStaleMs) {
                this.HistoryReadMs = NowMs;
                (P.Panel as AppAssistPanel).LoadHistory();
            }
            return AppTabBusRules.ShouldReload(OnList, this.IndexReadMs, NowMs);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSync.Visible");
            return false;
        }
    };
}

/** One gathered refresh coming due. A class, not a closure. */
export class AppTabSyncFire {
    private Owner: AppTabSync;
    private What: string;

    public constructor(Owner: AppTabSync, What: string) {
        this.Owner = Owner;
        this.What = What;
    }

    public Fire = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Owner.Due(this.What);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabSyncFire.Fire");
        }
    };
}

/** What `AppTabPageLink` needs from the page: a fresh snapshot of what is on screen. */
export interface IAppTabOwner {
    TabParts(): AppTabParts;
}

/** `IAppTabPage` over the page (`AppMain`). A class, not a closure. */
export class AppTabPageLink implements IAppTabPage {
    private Owner: IAppTabOwner;

    public constructor(Owner: IAppTabOwner) {
        this.Owner = Owner;
    }

    public Parts = (): AppTabParts => {
        if (getCoreUtils().KErrorMode === true) { return new AppTabParts(); }
        try {
            return this.Owner.TabParts();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTabPageLink.Parts");
            return new AppTabParts();
        }
    };

    /** DOCUMENTED EXCEPTION: the clock. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Now = (): Date => {
        return new Date();
    };
}
